import { jsx } from 'react/jsx-runtime';
import React, { useState, useRef, useCallback, useMemo } from 'react';
import TetherComponent from 'react-tether';
import { useKeyUp, useOutsideClick } from '../../utils/hooks.js';

const OverlayWrapper = ({
  attachmentConstraint = "together",
  behavior = "click",
  children,
  className,
  horizontalAttachment = "center",
  horizontalTargetAttachment = "auto",
  isConstrainedToScreen = false,
  onHide = () => true,
  onShow = () => true,
  overlay,
  shouldHideOnClick = true,
  testSection,
  verticalAttachment = "top",
  verticalTargetAttachment = "auto",
  ...restProps
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const show = () => {
    if (!isOpen && onShow() !== false) {
      setIsOpen(true);
    }
  };
  const hide = useCallback(() => {
    if (isOpen && onHide() !== false) {
      setIsOpen(false);
    }
  }, [isOpen, onHide]);
  const toggle = () => isOpen ? hide() : show();
  useKeyUp("Escape", hide);
  useOutsideClick(triggerRef, () => {
    if (shouldHideOnClick) {
      hide();
    }
  });
  const child = React.Children.only(children);
  const onChildClick = (event) => {
    toggle();
    if (child.props.onClick) {
      child.props.onClick(event);
    }
  };
  const onChildMouseOver = (event) => {
    show();
    if (child.props.onMouseOver) {
      child.props.onMouseOver(event);
    }
  };
  const onChildMouseOut = (event) => {
    hide();
    if (child.props.onMouseOut) {
      child.props.onMouseOut(event);
    }
  };
  const eventHandlerProps = useMemo(() => {
    if (behavior === "click") {
      return {
        onClick: onChildClick
      };
    } else if (behavior === "hover") {
      return {
        onMouseOut: onChildMouseOut,
        onMouseOver: onChildMouseOver
      };
    } else {
      throw new Error(
        `Unknown behavior: ${behavior} in OverlayWrapper component.`
      );
    }
  }, [behavior, isOpen]);
  const attachment = `${verticalAttachment} ${horizontalAttachment}`;
  const targetAttachment = `${verticalTargetAttachment} ${horizontalTargetAttachment}`;
  const overlayState = useMemo(() => {
    return {
      display: isOpen ? "block" : "none"
    };
  }, [isOpen]);
  return /* @__PURE__ */ jsx(
    "div",
    {
      className,
      "data-oui-component": true,
      "data-test-section": testSection,
      ref: triggerRef,
      style: { display: "inline-block" },
      children: /* @__PURE__ */ jsx(
        TetherComponent,
        {
          attachment,
          constraints: [
            {
              attachment: attachmentConstraint,
              pin: isConstrainedToScreen,
              to: "window"
            }
          ],
          renderElement: (ref) => /* @__PURE__ */ jsx("div", { ref, style: overlayState, children: overlay }),
          renderTarget: (ref) => /* @__PURE__ */ jsx(
            "div",
            {
              className: "click-area",
              ref,
              ...eventHandlerProps,
              "data-testid": "click-area",
              children: child
            }
          ),
          targetAttachment,
          ...restProps
        }
      )
    }
  );
};

export { OverlayWrapper as default };
